import { useState } from 'react';
import type { AccountInfo } from '../../../shared/protocol.ts';
import { account } from '../lib/account.ts';
import { Icon } from './common.tsx';

/**
 * The second step of signing in on a server that checks addresses: type the six-digit code that was emailed.
 * Used on the home page and in the green room.
 */
export function SignInCode({
  email,
  name,
  onDone,
  onCancel,
  cancelLabel = 'Use a different email',
}: {
  email: string;
  name?: string;
  onDone: (a: AccountInfo) => void;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const verify = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      onDone(await account.verify(email, value));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const resend = async () => {
    setError('');
    setNote('');
    try {
      await account.signIn(email, name);
      setNote('New code sent.');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="col" style={{ gap: 10 }}>
      <span className="small">
        We’ve emailed a 6-digit code to <strong>{email}</strong>. It may take a minute; check spam if it doesn’t arrive.
      </span>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input
          className="code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          aria-label="Sign-in code"
          maxLength={6}
          value={code}
          autoFocus
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(v);
            if (v.length === 6) void verify(v); // pasted or autofilled codes go straight through
          }}
          onKeyDown={(e) => e.key === 'Enter' && void verify()}
        />
        <button className="primary" onClick={() => void verify()} disabled={busy || code.length !== 6}>
          {busy && <span className="spinner" />} Continue
        </button>
      </div>
      <span className="field-hint">
        <button className="linklike" onClick={() => void resend()}>
          Send a new code
        </button>{' '}
        ·{' '}
        <button className="linklike" onClick={onCancel}>
          {cancelLabel}
        </button>
        {note && <> · {note}</>}
      </span>
      {error && (
        <div className="alert bad">
          <Icon name="warn" size={16} /> {error}
        </div>
      )}
    </div>
  );
}
